import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getCountries, getStatesByCountry } from './geo';
import { getCountryCode } from './utils';
// Ian's files, bundled with the app (src/assets) rather than placed in public/,
// so they are not served at a fixed /templates/ address.
import partnerTemplateUrl from '../assets/import_templates/Partner_Workspace_School_and_Network_Import_Template.xlsx';
import networkSchoolTemplateUrl from '../assets/import_templates/Network_Workspace_School_Import_Template.xlsx';
import networkClassTemplateUrl from '../assets/import_templates/Network_Workspace_Class_Import_Template.xlsx';
import schoolClassTemplateUrl from '../assets/import_templates/School_Workspace_Class_Import_Template.xlsx';

/**
 * The four import templates Ian shared on 13 Oct 2026 (tabs renamed Guide /
 * Template / Lookup on 14 Oct) — one file each for:
 *
 *   partner         Partner adding networks and schools
 *   network_school  School Network adding schools
 *   network_class   School Network adding classes and learners
 *   school_class    School adding classes and learners
 *
 * The files themselves live in src/assets/import_templates and are served UNCHANGED as the
 * "Download Template" file, so the guide sheet, the dropdowns (country → state,
 * grade, year…), the highlighting and the hidden Lookup sheet are exactly his.
 *
 * The same file is the export ("instead of having two different templates each
 * for importing and exporting"): the rows on file are written into a copy of
 * it, from row 5, leaving everything else as it is.
 *
 * Every sheet is laid out the same way: title on row 2, column headers on row
 * 3, hints on row 4, data from row 5. Columns are still found by header text,
 * never by position.
 */

export const TEMPLATE_PARTNER = 'partner';
export const TEMPLATE_NETWORK_SCHOOL = 'network_school';
export const TEMPLATE_NETWORK_CLASS = 'network_class';
export const TEMPLATE_SCHOOL_CLASS = 'school_class';

const FIRST_DATA_ROW = 5;
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** What a dropdown holds where there is nothing to choose (no state, no network). */
export const NOT_APPLICABLE = 'N/A';

const ACCOUNT_COLUMNS = [
    { key: 'contact_first_name', label: 'Contact First Name' },
    { key: 'contact_last_name', label: 'Contact Last Name' },
    { key: 'contact_title', label: 'Contact Title / Role' },
    { key: 'contact_email', label: 'Contact Email' },
    { key: 'country_code', label: 'Country Code' },
    { key: 'phone', label: 'Phone Number' },
    { key: 'website', label: 'Website' },
    { key: 'address1', label: 'Address - Line 1' },
    { key: 'address2', label: 'Address - Line 2' },
    { key: 'country', label: 'Country' },
    { key: 'state', label: 'State / Province / Region' },
    { key: 'city', label: 'City' },
    { key: 'postal_code', label: 'Postal Code' },
];

const CLASS_COLUMNS = [
    { key: 'class_name', label: 'Class Name' },
    { key: 'grade_level', label: 'Grade Level' },
    { key: 'academic_year', label: 'Academic Year' },
    { key: 'teacher_first_name', label: 'Teacher First Name' },
    { key: 'teacher_last_name', label: 'Teacher Last Name' },
    { key: 'teacher_email', label: 'Teacher Email' },
    { key: 'learner_first_name', label: 'Learner First Name' },
    { key: 'learner_last_name', label: 'Learner Last Name' },
    { key: 'year_of_birth', label: 'Year of Birth', number: true },
    { key: 'learner_email', label: 'Learner Email' },
    { key: 'parent_email', label: 'Parent Email' },
    { key: 'gender', label: 'Gender' },
    { key: 'country', label: 'Country' },
    { key: 'state', label: 'State / Province / Region' },
    { key: 'city', label: 'City' },
    { key: 'postal_code', label: 'Postal Code' },
];

export const IMPORT_TEMPLATES = {
    [TEMPLATE_PARTNER]: {
        file: 'Partner_Workspace_School_and_Network_Import_Template.xlsx',
        url: partnerTemplateUrl,
        sheet: 'Template',
        columns: [
            { key: 'type', label: 'Type' },
            { key: 'parent_network_email', label: 'Parent Network Email' },
            { key: 'org_name', label: 'School or Network Name' },
            ...ACCOUNT_COLUMNS,
        ],
        // The two headers no other template has.
        marks: ['Type', 'Parent Network Email'],
    },
    [TEMPLATE_NETWORK_SCHOOL]: {
        file: 'Network_Workspace_School_Import_Template.xlsx',
        url: networkSchoolTemplateUrl,
        sheet: 'Template',
        columns: [{ key: 'org_name', label: 'School Name' }, ...ACCOUNT_COLUMNS],
        marks: ['School Name', 'Contact First Name'],
    },
    [TEMPLATE_NETWORK_CLASS]: {
        file: 'Network_Workspace_Class_Import_Template.xlsx',
        url: networkClassTemplateUrl,
        sheet: 'Template',
        columns: [{ key: 'school_email', label: 'School Email' }, ...CLASS_COLUMNS],
        marks: ['School Email', 'Learner First Name'],
    },
    [TEMPLATE_SCHOOL_CLASS]: {
        file: 'School_Workspace_Class_Import_Template.xlsx',
        url: schoolClassTemplateUrl,
        sheet: 'Template',
        columns: CLASS_COLUMNS,
        marks: ['Class Name', 'Learner First Name'],
    },
};

/** The learner columns: a row with all of them empty is a class with no learners yet. */
export const LEARNER_KEYS = ['learner_first_name', 'learner_last_name', 'year_of_birth', 'learner_email',
    'parent_email', 'gender', 'country', 'state', 'city', 'postal_code'];

const text = (v) => (v == null || typeof v === 'object' ? '' : String(v).trim());
const norm = (v) => text(v).toLowerCase().replace(/\s+/g, ' ');

const templateUrl = (kind) => IMPORT_TEMPLATES[kind].url;

const fetchTemplate = async (kind) => {
    const res = await fetch(templateUrl(kind));
    if (!res.ok) throw new Error('Could not load the template file.');
    return res.arrayBuffer();
};

/** Download Template — Ian's file, byte for byte. */
export const downloadImportTemplate = async (kind) => {
    const buf = await fetchTemplate(kind);
    saveAs(new Blob([buf], { type: XLSX_MIME }), IMPORT_TEMPLATES[kind].file);
};

/* ───────────────────────────── the Lookup sheet ───────────────────────────── */

/**
 * The hidden Lookup sheet: country name ↔ ISO code ↔ "Country (+dial)". Read
 * from the file in hand, so a name on the country dropdown always resolves to
 * the code the API stores — including the few the portal's own list spells
 * differently ("Cote d'Ivoire").
 */
const readLookup = (wb) => {
    const out = {
        isoByName: new Map(), nameByIso: new Map(), dialTextByIso: new Map(),
        // The dropdowns' own lists, by header: a value typed or pasted past the
        // dropdown is checked against the same list Excel would.
        lists: {}, regionsByCountry: new Map(),
    };
    const ws = wb.Sheets[wb.SheetNames.find((n) => /^lookup$/i.test(n))];
    if (!ws) return out;
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    const head = (rows[0] || []).map(norm);
    const cName = head.indexOf('country');
    const cIso = head.indexOf('iso code');
    const cDial = head.indexOf('dial code');
    if (cName < 0 || cIso < 0) return out;
    rows.slice(1).forEach((r) => {
        const name = text(r[cName]);
        const iso = text(r[cIso]).toUpperCase();
        if (!name || !iso) return;
        out.isoByName.set(norm(name), iso);
        out.nameByIso.set(iso, name);
    });
    // Lists: Dial code, Grade Level, Academic Year, Gender, Year of Birth, Type (columns A-J).
    head.slice(0, 10).forEach((h, i) => {
        if (!h || i === cName || i === cIso) return;
        out.lists[h] = new Set(rows.slice(1).map((r) => norm(r[i])).filter(Boolean));
    });
    // Regions: from column K, one column per country, headed by its name.
    head.forEach((h, i) => {
        if (i < 10 || !h) return;
        out.regionsByCountry.set(h, new Set(rows.slice(1).map((r) => norm(r[i])).filter(Boolean)));
    });
    // The dial list is its own column, in its own order: match by the name before "(+…)".
    if (cDial >= 0) {
        rows.slice(1).forEach((r) => {
            const dialText = text(r[cDial]);
            const iso = out.isoByName.get(norm(dialText.replace(/\s*\(\+?\d+\)\s*$/, '')));
            if (dialText && iso) out.dialTextByIso.set(iso, dialText);
        });
    }
    return out;
};

/** Is a value on one of the template's dropdown lists? Unknown list (an older file) → true. */
export const onTemplateList = (lookup, listName, value) => {
    const list = lookup && lookup.lists && lookup.lists[norm(listName)];
    return !list || !list.size || list.has(norm(value));
};

/**
 * Is a State / Province / Region one of the selected country's — the rule the
 * template highlights in red. N/A is that list's entry for a country with no
 * regions. A country the Lookup does not list is not judged here.
 */
export const regionBelongsToCountry = (lookup, countryValue, region) => {
    if (!lookup || !lookup.regionsByCountry || !lookup.regionsByCountry.size) return true;
    const iso = countryToIso(lookup, countryValue);
    const name = iso ? (lookup.nameByIso.get(iso) || countryValue) : countryValue;
    const regions = lookup.regionsByCountry.get(norm(name));
    return !regions || regions.has(norm(region));
};

/**
 * A region picked from the template → the code the portal's own State pickers
 * save (e.g. "Central Singapore" → "SG-01"), so the school / learner opens with
 * its state selected. N/A (no regions) → no state.
 */
export const stateToCode = (iso, state) => {
    const v = blankIfNA(state);
    if (!v || !iso) return v;
    const hit = getStatesByCountry(iso).find((st) => st.code === v || norm(st.name) === norm(v));
    return hit ? hit.code : v;
};

/**
 * The State to write in an export: the region's name as the dropdown lists it
 * (the portal saves a code, e.g. "SG-01"), or N/A — the dropdown's own entry —
 * for a country with no regions, which is saved as no state at all.
 */
export const stateForExport = (lookup, countryValue, state) => {
    const iso = lookup && text(countryValue) ? countryToIso(lookup, countryValue) : '';
    if (text(state)) {
        const hit = iso && getStatesByCountry(iso).find((st) => st.code === text(state));
        return hit ? hit.name : text(state);
    }
    if (!lookup || !lookup.regionsByCountry || !text(countryValue)) return '';
    const regions = lookup.regionsByCountry.get(norm(iso ? (lookup.nameByIso.get(iso) || countryValue) : countryValue));
    return regions && regions.size === 1 && regions.has(norm(NOT_APPLICABLE)) ? NOT_APPLICABLE : '';
};

/** A country as the template lists it (or as typed: a name or a two-letter code) → ISO code, or ''. */
export const countryToIso = (lookup, value) => {
    const raw = text(value);
    if (!raw) return '';
    const viaLookup = lookup && lookup.isoByName.get(norm(raw));
    if (viaLookup) return viaLookup;
    const code = String(getCountryCode(raw) || '').toUpperCase();
    return getCountries().some((c) => c.code === code) ? code : '';
};

/** "Singapore (+65)" → "+65". A bare "+65" / "65" is taken as typed. */
export const dialFromCountryCode = (value) => {
    const raw = text(value);
    if (!raw) return '';
    const m = raw.match(/\(\s*\+?(\d{1,4})\s*\)/) || raw.replace(/\s+/g, '').match(/^(?:\+|00)?(\d{1,4})$/);
    return m ? `+${m[1]}` : raw;
};

/** "N/A" on a dropdown means "nothing here". */
export const blankIfNA = (v) => (norm(v) === 'n/a' ? '' : text(v));

/* ────────────────────────────── reading a file ────────────────────────────── */

/**
 * Which of the four templates a workbook is, or null for any other file (an
 * older template, an export from before, a hand-made sheet).
 * @returns {{ kind: string, sheetName: string, headerRow: number } | null}
 */
export const detectImportTemplate = (wb, kinds = Object.keys(IMPORT_TEMPLATES)) => {
    for (const sheetName of wb.SheetNames) {
        if (/^(guide|lookup)$/i.test(sheetName)) continue;
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '' });
        for (let i = 0; i < Math.min(aoa.length, 12); i += 1) {
            const cells = (aoa[i] || []).map(norm);
            // Most specific first, so the partner and network files are never read as a plainer one.
            const kind = [TEMPLATE_PARTNER, TEMPLATE_NETWORK_CLASS, TEMPLATE_NETWORK_SCHOOL, TEMPLATE_SCHOOL_CLASS]
                .filter((k) => kinds.includes(k))
                .find((k) => IMPORT_TEMPLATES[k].marks.every((m) => cells.includes(norm(m))));
            if (kind) return { kind, sheetName, headerRow: i };
        }
    }
    return null;
};

/**
 * The data rows of a template sheet, one object per row keyed by column `key`,
 * plus `_row` (the row number as Excel shows it).
 * @returns {{ rows: object[], missing: string[], lookup: object }}
 */
export const readImportTemplateRows = (wb, found) => {
    const def = IMPORT_TEMPLATES[found.kind];
    const sheet = wb.Sheets[found.sheetName];
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    // Row numbers as Excel shows them: the sheet's range may not start at row 1.
    const firstRow = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r : 0;
    const header = (aoa[found.headerRow] || []).map(norm);
    const map = def.columns.map((c) => ({ ...c, idx: header.indexOf(norm(c.label)) }));
    const hint = /^(required|optional)\b/;
    const rows = [];
    for (let i = found.headerRow + 1; i < aoa.length; i += 1) {
        const row = aoa[i] || [];
        const values = { _row: i + firstRow + 1 };
        map.forEach((c) => { values[c.key] = c.idx >= 0 ? text(row[c.idx]) : ''; });
        const filled = def.columns.filter((c) => values[c.key] !== '');
        if (!filled.length) continue;
        // The hint row under the headers ("Required", "Required (dropdown)", "Optional").
        if (filled.every((c) => hint.test(norm(values[c.key])))) continue;
        // The partner file fills Parent Network Email by formula: a row holding nothing else is empty.
        if (filled.length === 1 && filled[0].key === 'parent_network_email') continue;
        rows.push(values);
    }
    return { rows, missing: map.filter((c) => c.idx < 0).map((c) => c.label), lookup: readLookup(wb) };
};

/* ─────────────────── classes and learners (the two class files) ─────────────────── */

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ''));
const GENDERS = ['male', 'female', 'other'];

/**
 * The rows of a class template, checked against its own Guide sheet.
 *
 * Each row is one learner; rows sharing the class details (and, in the network
 * file, the School Email) are one class; a row with every learner column empty
 * is a class with no learners yet. Problems come back as plain sentences, by
 * the row number Excel shows.
 *
 * @returns {{ classes: Array<{ key, school_email, class_name, grade_level, academic_year,
 *              teacher_first_name, teacher_last_name, teacher_email, _row, learners: object[] }>,
 *             problems: string[] }}
 */
export const readClassImportRows = (wb, found) => {
    const { rows, missing, lookup } = readImportTemplateRows(wb, found);
    const problems = [];
    if (missing.length) problems.push(`Column missing: ${missing.join(', ')} — keep the template's columns as they are.`);
    const network = found.kind === TEMPLATE_NETWORK_CLASS;
    const classes = new Map();
    const say = (row, column, message) => problems.push(`Row ${row}, ${column}: ${message}`);

    rows.forEach((r) => {
        const required = [
            ...(network ? [['school_email', 'School Email']] : []),
            ['class_name', 'Class Name'], ['grade_level', 'Grade Level'], ['academic_year', 'Academic Year'],
            ['teacher_first_name', 'Teacher First Name'], ['teacher_last_name', 'Teacher Last Name'], ['teacher_email', 'Teacher Email'],
        ];
        required.forEach(([k, label]) => { if (!r[k]) say(r._row, label, 'Required'); });
        if (network && r.school_email && !isEmail(r.school_email)) say(r._row, 'School Email', 'Invalid email address');
        if (r.teacher_email && !isEmail(r.teacher_email)) say(r._row, 'Teacher Email', 'Invalid email address');
        if (r.grade_level && !onTemplateList(lookup, 'Grade Level', r.grade_level)) say(r._row, 'Grade Level', `"${r.grade_level}" — select a grade from the list`);
        if (r.academic_year && !onTemplateList(lookup, 'Academic Year', r.academic_year)) say(r._row, 'Academic Year', `"${r.academic_year}" — select a year from the list`);

        const hasLearner = LEARNER_KEYS.some((k) => r[k]);
        let learner = null;
        if (hasLearner) {
            [['learner_first_name', 'Learner First Name'], ['learner_last_name', 'Learner Last Name'], ['year_of_birth', 'Year of Birth'],
                ['learner_email', 'Learner Email'], ['parent_email', 'Parent Email'], ['gender', 'Gender'], ['country', 'Country'],
                ['state', 'State / Province / Region'], ['city', 'City'], ['postal_code', 'Postal Code']]
                .forEach(([k, label]) => { if (!r[k]) say(r._row, label, k === 'state' ? `Required — select ${NOT_APPLICABLE} if the country has no regions` : 'Required'); });
            if (r.learner_email && !isEmail(r.learner_email)) say(r._row, 'Learner Email', 'Invalid email address');
            if (r.parent_email && !isEmail(r.parent_email)) say(r._row, 'Parent Email', 'Invalid email address');
            if (r.gender && !GENDERS.includes(norm(r.gender))) say(r._row, 'Gender', `"${r.gender}" — select Male, Female or Other`);
            if (r.year_of_birth && (!/^\d{4}$/.test(r.year_of_birth) || !onTemplateList(lookup, 'Year of Birth', r.year_of_birth))) say(r._row, 'Year of Birth', `"${r.year_of_birth}" — select a year from the list`);
            const iso = countryToIso(lookup, r.country);
            if (r.country && !iso) say(r._row, 'Country', `"${r.country}" is not on the Country list — select it from the dropdown`);
            else if (r.country && r.state && !regionBelongsToCountry(lookup, r.country, r.state)) say(r._row, 'State / Province / Region', `"${r.state}" is not a region of ${r.country} — select Country first, then a region from the list`);
            learner = {
                _row: r._row,
                first_name: r.learner_first_name,
                last_name: r.learner_last_name,
                year_of_birth: r.year_of_birth,
                learner_email: r.learner_email,
                parent_email: r.parent_email,
                gender: norm(r.gender),
                country: iso,
                state: stateToCode(iso, r.state),
                city: r.city,
                postal_code: r.postal_code,
            };
        }

        // One class per Class Name (and School Email in the network file). The
        // template highlights a second teacher on the same class as a mistake,
        // so a different Teacher Email is reported below, not made a new class.
        const key = [network ? norm(r.school_email) : '', norm(r.class_name)].join('|');
        if (!classes.has(key)) {
            classes.set(key, {
                key,
                _row: r._row,
                school_email: r.school_email,
                class_name: r.class_name,
                grade_level: r.grade_level,
                academic_year: r.academic_year,
                teacher_first_name: r.teacher_first_name,
                teacher_last_name: r.teacher_last_name,
                teacher_email: r.teacher_email,
                learners: [],
            });
        } else {
            // The template highlights a class whose rows disagree; the upload says which.
            const c = classes.get(key);
            [['grade_level', 'Grade Level'], ['academic_year', 'Academic Year'], ['teacher_first_name', 'Teacher First Name'],
                ['teacher_last_name', 'Teacher Last Name'], ['teacher_email', 'Teacher Email']].forEach(([k, label]) => {
                if (r[k] && c[k] && norm(r[k]) !== norm(c[k])) say(r._row, label, `"${r[k]}" — class "${c.class_name}" has "${c[k]}" on row ${c._row}; every row of a class must match`);
            });
        }
        if (learner) classes.get(key).learners.push(learner);
    });
    if (!rows.length) problems.push('The file has no rows — fill in at least one class.');
    return { classes: [...classes.values()], problems };
};

/* ─────────────────────── writing rows into the template ─────────────────────── */

const escapeXml = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // control characters a spreadsheet cell cannot hold
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

const colLetter = (i) => XLSX.utils.encode_col(i);

/**
 * Excel stores a formula repeated down a column once ("shared"): the first cell
 * holds it, the others point back to it. Writing a value over that first cell
 * would leave the rest pointing at nothing, and Excel would ask to repair the
 * file. So every shared formula is written out in full, cell by cell, first —
 * the same formula, its relative rows moved down as Excel itself would.
 */
const expandSharedFormulas = (sheetXml) => {
    const masters = {};
    const masterRe = /<c r="[A-Z]+(\d+)"[^>]*>\s*<f t="shared" ref="[^"]*" si="(\d+)"[^>]*>([\s\S]*?)<\/f>/g;
    let m = masterRe.exec(sheetXml);
    while (m) {
        masters[m[2]] = { row: Number(m[1]), f: m[3] };
        m = masterRe.exec(sheetXml);
    }
    if (!Object.keys(masters).length) return sheetXml;
    // Relative rows move with the cell; a $-anchored row does not. Function
    // names ("LOG10(") are not cell references.
    const shift = (f, delta) => f.replace(/(\$?)([A-Z]{1,3})(\$?)(\d+)(?![\d(])/g,
        (ref, d1, col, d2, row) => (d2 ? ref : `${d1}${col}${Number(row) + delta}`));
    return sheetXml
        // the first cell: the same formula, no longer shared
        .replace(/<f t="shared" ref="[^"]*" si="\d+"[^>]*>([\s\S]*?)<\/f>/g, (all, f) => `<f>${f}</f>`)
        // the others: their own copy
        .replace(/(<c r="[A-Z]+(\d+)"[^>]*>\s*)<f t="shared" si="(\d+)"\s*\/>/g, (all, open, row, si) => {
            const master = masters[si];
            return master ? `${open}<f>${shift(master.f, Number(row) - master.row)}</f>` : all;
        });
};

/** The cells of one <row>, by column letter. */
const cellsByColumn = (rowXml) => {
    const out = {};
    (rowXml.match(/<c [^>]*\/>|<c [^>]*>[\s\S]*?<\/c>/g) || []).forEach((c) => {
        const col = (c.match(/ r="([A-Z]+)\d+"/) || [])[1];
        if (col) out[col] = c;
    });
    return out;
};

/** The sheet's file inside the workbook, by its tab name. */
const sheetPath = (workbookXml, relsXml, sheetName) => {
    const sheetTag = (workbookXml.match(/<sheet [^>]*>/g) || []).find((t) => t.includes(`name="${sheetName}"`));
    const rid = sheetTag && (sheetTag.match(/r:id="([^"]+)"/) || [])[1];
    const rel = rid && (relsXml.match(/<Relationship [^>]*>/g) || []).find((t) => t.includes(`Id="${rid}"`));
    const target = rel && (rel.match(/Target="([^"]+)"/) || [])[1];
    if (!target) throw new Error(`The template has no "${sheetName}" sheet.`);
    return `xl/${target.replace(/^\/?xl\//, '').replace(/^\//, '')}`;
};

/**
 * A copy of the template with `rows` written from row 5. Each row is an object
 * keyed by column `key`; a missing or empty value leaves the cell empty.
 * @param {string} [as]  JSZip output type; the browser default is a Blob.
 * @returns {Promise<Blob>}
 */
export const fillImportTemplate = async (kind, rows = [], as = 'blob') => {
    const def = IMPORT_TEMPLATES[kind];
    const buf = await fetchTemplate(kind);
    const zip = await JSZip.loadAsync(buf);
    const workbookPath = 'xl/workbook.xml';
    let workbookXml = await zip.file(workbookPath).async('string');
    const relsXml = await zip.file('xl/_rels/workbook.xml.rels').async('string');
    const path = sheetPath(workbookXml, relsXml, def.sheet);
    let xml = expandSharedFormulas(await zip.file(path).async('string'));

    const dataStart = xml.indexOf('<sheetData>');
    const dataEnd = xml.indexOf('</sheetData>');
    if (dataStart < 0 || dataEnd < 0) throw new Error('The template sheet could not be read.');
    const rowXmls = xml.slice(dataStart + '<sheetData>'.length, dataEnd).match(/<row [^>]*>[\s\S]*?<\/row>|<row [^>]*\/>/g) || [];
    const byNumber = new Map(rowXmls.map((r) => [Number((r.match(/ r="(\d+)"/) || [])[1]), r]));
    const lastTemplateRow = Math.max(...byNumber.keys());

    // The style of each column's data cells, from the template's own second data row.
    const sample = byNumber.get(FIRST_DATA_ROW + 1) || byNumber.get(FIRST_DATA_ROW) || '';
    const styleOf = {};
    (sample.match(/<c [^>]*>/g) || []).forEach((c) => {
        const ref = (c.match(/ r="([A-Z]+)\d+"/) || [])[1];
        const s = (c.match(/ s="(\d+)"/) || [])[1];
        if (ref && s) styleOf[ref] = s;
    });

    rows.forEach((values, i) => {
        const n = FIRST_DATA_ROW + i;
        const existingRow = byNumber.get(n) || '';
        const existing = cellsByColumn(existingRow);
        const written = new Set();
        const cells = def.columns.map((c, ci) => {
            const col = colLetter(ci);
            const ref = `${col}${n}`;
            written.add(col);
            const style = (existing[col] && (existing[col].match(/ s="(\d+)"/) || [])[1]) || styleOf[col];
            const s = style ? ` s="${style}"` : '';
            const v = values[c.key];
            // Nothing to write: the template's own cell stays — its style, and its
            // formula where it has one (the partner file's N/A for a network row).
            if (v === undefined || v === null || String(v).trim() === '') return existing[col] || `<c r="${ref}"${s}/>`;
            if (c.number && /^\d+$/.test(String(v).trim())) return `<c r="${ref}"${s}><v>${String(v).trim()}</v></c>`;
            return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${escapeXml(v)}</t></is></c>`;
        });
        // Cells right of the columns (the template's edge styling) stay as they are.
        const extra = Object.keys(existing).filter((col) => !written.has(col))
            .sort((a, b) => XLSX.utils.decode_col(a) - XLSX.utils.decode_col(b))
            .map((col) => existing[col]);
        const open = (existingRow || `<row r="${n}">`).match(/<row [^>]*?\/?>/)[0].replace(/\/>$/, '>');
        byNumber.set(n, `${open}${[...cells, ...extra].join('')}</row>`);
    });

    const lastRow = Math.max(lastTemplateRow, FIRST_DATA_ROW + rows.length - 1);
    const sheetData = [...byNumber.keys()].sort((a, b) => a - b).map((n) => byNumber.get(n)).join('');
    let head = xml.slice(0, dataStart);
    let tail = xml.slice(dataEnd + '</sheetData>'.length);
    if (lastRow > lastTemplateRow) {
        // More rows than the template came with: the dropdowns, the highlighting
        // and the sheet's range reach down to the last row written.
        const grow = (s) => s.replace(new RegExp(`(\\$?[A-Z]{1,3}\\$?)${lastTemplateRow}(?!\\d)`, 'g'), `$1${lastRow}`);
        head = grow(head);
        tail = grow(tail);
    }
    xml = `${head}<sheetData>${sheetData}</sheetData>${tail}`;
    zip.file(path, xml);

    // The calculation chain lists the formula cells as they were. Excel builds
    // it again; a stale one makes it offer to repair the file.
    if (zip.file('xl/calcChain.xml')) {
        zip.remove('xl/calcChain.xml');
        const ctPath = '[Content_Types].xml';
        const ct = await zip.file(ctPath).async('string');
        zip.file(ctPath, ct.replace(/<Override [^>]*PartName="\/xl\/calcChain\.xml"[^>]*\/>/, ''));
        zip.file('xl/_rels/workbook.xml.rels', relsXml.replace(/<Relationship [^>]*Target="[^"]*calcChain\.xml"[^>]*\/>/, ''));
    }

    // The Lookup sheet's formulas (the partner file's "networks in this file"
    // list) are worked out again when the file is opened.
    if (!/fullCalcOnLoad/.test(workbookXml)) {
        workbookXml = /<calcPr[^>]*\/>/.test(workbookXml)
            ? workbookXml.replace(/<calcPr([^>]*)\/>/, '<calcPr$1 fullCalcOnLoad="1"/>')
            : workbookXml.replace('</workbook>', '<calcPr fullCalcOnLoad="1"/></workbook>');
        zip.file(workbookPath, workbookXml);
    }
    return zip.generateAsync({ type: as, mimeType: XLSX_MIME, compression: 'DEFLATE' });
};

/** The template's own country names and dial texts, for an export. */
export const loadTemplateLookup = async (kind) => readLookup(XLSX.read(await fetchTemplate(kind), { type: 'array' }));

export const saveTemplateBlob = (blob, filename) => saveAs(blob, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);

/** ISO code (or whatever is on file) → the name on the template's Country dropdown. */
export const countryNameFor = (lookup, value) => {
    const raw = text(value);
    if (!raw) return '';
    const iso = countryToIso(lookup, raw) || raw.toUpperCase();
    return (lookup && lookup.nameByIso.get(iso)) || (getCountries().find((c) => c.code === iso) || {}).label || raw;
};

/** A dial code and country on file → the text on the template's Country Code dropdown. */
export const countryCodeTextFor = (lookup, dial, country) => {
    const d = dialFromCountryCode(dial);
    if (!d) return '';
    const iso = countryToIso(lookup, country);
    const own = iso && lookup && lookup.dialTextByIso.get(iso);
    if (own && own.includes(`(${d})`)) return own;
    // The phone's country is not always the address's: any entry with this dial code.
    const any = lookup && [...lookup.dialTextByIso.values()].find((t) => t.includes(`(${d})`));
    return any || d;
};
